import React, { memo } from 'react';
import { AlertTriangle, CheckCircle, Lightbulb, Map, Home, Plane } from 'lucide-react';
export function SalesLetter() {
  return <article className="max-w-3xl mx-auto px-4 py-12 text-lg text-slate-800 leading-relaxed" data-id="element-111">
      {/* Hook / Intro */}
      <div className="mb-12" data-id="element-112">
        <p className="font-bold text-2xl mb-6 text-blue-900" data-id="element-113">
          Si alguna vez entraste al aula sabiendo que tenías que explicar
          Sistemas de Información Contable… pero sin estar del todo seguro de
          cómo hacerlo claro, esto es para vos.
        </p>
        <p className="mb-4" data-id="element-114">
          No porque no sepas contabilidad. No porque no tengas formación. No
          porque no te importe enseñar bien.
        </p>
        <p className="mb-4 font-medium italic border-l-4 border-orange-500 pl-4 py-1 bg-orange-50" data-id="element-115">
          Sino porque nadie te dio una herramienta pensada para tu realidad
          docente.
        </p>
      </div>

      {/* The Problem */}
      <div className="mb-16" data-id="element-116">
        <h2 className="text-3xl font-bold text-slate-900 mb-6" data-id="element-117">
          El problema no es la contabilidad
        </h2>
        <p className="mb-4" data-id="element-118">El problema es cómo nos obligaron a enseñarla.</p>
        <p className="mb-4" data-id="element-119">
          La contabilidad está en todos lados. En el precio del colectivo. En el
          sueldo que no alcanza. En la inflación que aparece en las noticias. En
          las decisiones que toman las familias todos los días.
        </p>
        <p className="mb-6" data-id="element-120">Y sin embargo… En el aula pasa esto:</p>

        <ul className="space-y-3 mb-8 bg-red-50 p-6 rounded-lg border border-red-100" data-id="element-121">
          <li className="flex items-center gap-3" data-id="element-122">
            <AlertTriangle className="text-red-500 w-6 h-6 flex-shrink-0" data-id="element-123" />
            <span data-id="element-124">Alumnos desconectados</span>
          </li>
          <li className="flex items-center gap-3" data-id="element-125">
            <AlertTriangle className="text-red-500 w-6 h-6 flex-shrink-0" data-id="element-126" />
            <span data-id="element-127">Miradas perdidas</span>
          </li>
          <li className="flex items-center gap-3" data-id="element-128">
            <AlertTriangle className="text-red-500 w-6 h-6 flex-shrink-0" data-id="element-129" />
            <span data-id="element-130">Celulares bajo el banco</span>
          </li>
          <li className="flex items-center gap-3 mt-2 font-bold text-red-700" data-id="element-131">
            La pregunta inevitable: “Profe… ¿esto para qué sirve?”
          </li>
        </ul>

        <p className="mb-4" data-id="element-132">
          Y ahí aparece la frustración. Porque vos sabés que la materia es
          importante. Porque querés que entiendan. Porque te esforzás.
        </p>
        <p className="mb-4 font-bold" data-id="element-133">Pero algo no baja.</p>
      </div>

      {/* The Core Issue */}
      <div className="mb-16" data-id="element-134">
        <h3 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-135">
          Lo que nadie dice en voz alta
        </h3>
        <p className="mb-4 text-xl" data-id="element-136">
          El problema <span className="bg-yellow-200 px-1" data-id="element-137">no sos vos.</span>
        </p>
        <p className="mb-6" data-id="element-138">
          El problema es que Sistemas de Información Contable se enseña como si
          el alumno ya entendiera el sistema. Se empieza por definiciones. Por
          conceptos aislados. Por teoría.
        </p>
        <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-600 mb-6" data-id="element-139">
          <p className="font-bold mb-2 text-blue-900" data-id="element-140">
            Cuando en realidad debería empezarse por:
          </p>
          <ul className="list-disc list-inside space-y-1 text-blue-800" data-id="element-141">
            <li data-id="element-142">La función</li>
            <li data-id="element-143">La lógica</li>
            <li data-id="element-144">El para qué</li>
          </ul>
        </div>
      </div>

      {/* The Solution: SIC 360 */}
      <div className="mb-16" data-id="element-145">
        <div className="text-center mb-8" data-id="element-146">
          <span className="inline-block bg-blue-100 text-blue-800 px-4 py-1 rounded-full text-sm font-bold mb-2" data-id="element-147">
            LA SOLUCIÓN
          </span>
          <h2 className="text-4xl font-extrabold text-blue-900" data-id="element-148">
            Y ahí aparece SIC 360
          </h2>
        </div>

        <p className="mb-4" data-id="element-149">
          SIC 360 no es un libro. No es un PDF más. No es un PowerPoint
          genérico.
        </p>
        <p className="mb-6 font-bold text-xl text-slate-900" data-id="element-150">
          Es un sistema de clases listas, pensado específicamente para docentes
          de secundaria.
        </p>

        <div className="bg-white shadow-lg rounded-xl p-8 border border-slate-200 mb-8" data-id="element-151">
          <h4 className="font-bold text-lg mb-4 text-center" data-id="element-152">
            Un enfoque circular y progresivo que cambia la lógica:
          </h4>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left" data-id="element-153">
            <div className="flex-1" data-id="element-154">
              <p className="text-sm text-slate-500 uppercase tracking-wide" data-id="element-155">
                Del
              </p>
              <p className="font-bold text-blue-600 text-lg" data-id="element-156">PARA QUÉ</p>
            </div>
            <div className="text-slate-300" data-id="element-157">→</div>
            <div className="flex-1" data-id="element-158">
              <p className="text-sm text-slate-500 uppercase tracking-wide" data-id="element-159">
                Al
              </p>
              <p className="font-bold text-blue-600 text-lg" data-id="element-160">CÓMO</p>
            </div>
            <div className="text-slate-300" data-id="element-161">→</div>
            <div className="flex-1" data-id="element-162">
              <p className="text-sm text-slate-500 uppercase tracking-wide" data-id="element-163">
                Al
              </p>
              <p className="font-bold text-blue-600 text-lg" data-id="element-164">QUÉ</p>
            </div>
          </div>
          <div className="h-px bg-slate-100 my-6" data-id="element-165"></div>
          <div className="grid md:grid-cols-2 gap-4" data-id="element-166">
            <div className="flex items-center gap-2" data-id="element-167">
              <CheckCircle className="text-green-500 w-5 h-5" data-id="element-168" />
              <span data-id="element-169">De lo concreto a lo conceptual</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-170">
              <CheckCircle className="text-green-500 w-5 h-5" data-id="element-171" />
              <span data-id="element-172">De la lógica del sistema a la técnica</span>
            </div>
          </div>
          <div className="mt-6 text-center font-bold text-orange-600 text-lg" data-id="element-173">
            No memorizar. 👉 Entender el sistema completo.
          </div>
        </div>
      </div>

      {/* Transformation */}
      <div className="mb-16" data-id="element-174">
        <h3 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-175">
          ¿Qué cambia cuando enseñás con SIC 360?
        </h3>
        <p className="mb-6" data-id="element-176">
          Entrás al aula con la clase ya resuelta. Sabés exactamente qué
          explicar, cómo y en qué orden.
        </p>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-177">
          <div className="bg-green-50 p-6 rounded-lg" data-id="element-178">
            <h4 className="font-bold text-green-800 mb-3" data-id="element-179">Los alumnos:</h4>
            <ul className="space-y-2" data-id="element-180">
              <li className="flex items-center gap-2" data-id="element-181">
                <div className="w-2 h-2 bg-green-500 rounded-full" data-id="element-182"></div>
                <span data-id="element-183">Entienden</span>
              </li>
              <li className="flex items-center gap-2" data-id="element-184">
                <div className="w-2 h-2 bg-green-500 rounded-full" data-id="element-185"></div>
                <span data-id="element-186">Participan</span>
              </li>
              <li className="flex items-center gap-2" data-id="element-187">
                <div className="w-2 h-2 bg-green-500 rounded-full" data-id="element-188"></div>
                <span data-id="element-189">Conectan</span>
              </li>
            </ul>
          </div>

          <div className="bg-blue-50 p-6 rounded-lg" data-id="element-190">
            <h4 className="font-bold text-blue-800 mb-3" data-id="element-191">Vos:</h4>
            <ul className="space-y-2" data-id="element-192">
              <li className="flex items-center gap-2" data-id="element-193">
                <div className="w-2 h-2 bg-blue-500 rounded-full" data-id="element-194"></div>
                <span data-id="element-195">Enseñás con seguridad</span>
              </li>
              <li className="flex items-center gap-2" data-id="element-196">
                <div className="w-2 h-2 bg-blue-500 rounded-full" data-id="element-197"></div>
                <span data-id="element-198">Dejás de improvisar</span>
              </li>
              <li className="flex items-center gap-2" data-id="element-199">
                <div className="w-2 h-2 bg-blue-500 rounded-full" data-id="element-200"></div>
                <span data-id="element-201">Volvés a disfrutar enseñar</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Analogies */}
      <div className="mb-16" data-id="element-202">
        <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center" data-id="element-203">
          Pensalo así
        </h3>
        <div className="grid gap-6" data-id="element-204">
          <div className="flex items-start gap-4 p-4 bg-white border border-slate-100 rounded-lg shadow-sm" data-id="element-205">
            <div className="bg-orange-100 p-3 rounded-full" data-id="element-206">
              <Map className="w-6 h-6 text-orange-600" data-id="element-207" />
            </div>
            <div data-id="element-208">
              <h4 className="font-bold text-lg" data-id="element-209">Un GPS educativo</h4>
              <p className="text-slate-600" data-id="element-210">
                Sabés el recorrido. No improvisás.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-white border border-slate-100 rounded-lg shadow-sm" data-id="element-211">
            <div className="bg-blue-100 p-3 rounded-full" data-id="element-212">
              <Home className="w-6 h-6 text-blue-600" data-id="element-213" />
            </div>
            <div data-id="element-214">
              <h4 className="font-bold text-lg" data-id="element-215">Un plano de una casa</h4>
              <p className="text-slate-600" data-id="element-216">
                Primero cimientos. Después estructura.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-white border border-slate-100 rounded-lg shadow-sm" data-id="element-217">
            <div className="bg-indigo-100 p-3 rounded-full" data-id="element-218">
              <Plane className="w-6 h-6 text-indigo-600" data-id="element-219" />
            </div>
            <div data-id="element-220">
              <h4 className="font-bold text-lg" data-id="element-221">Un manual de piloto</h4>
              <p className="text-slate-600" data-id="element-222">
                Podés volar igual… pero con esto todo es más seguro.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Origin Story */}
      <div className="mb-12 bg-slate-50 p-8 rounded-xl border border-slate-200" data-id="element-223">
        <h3 className="text-xl font-bold text-slate-900 mb-4" data-id="element-224">
          SIC 360 nació de una frustración real
        </h3>
        <p className="mb-4 text-slate-700" data-id="element-225">
          De ver docentes preparados, con vocación, agotados. De clases que no
          funcionaban. De programas imposibles de bajar a tierra. De alumnos
          desconectados.
        </p>
        <p className="mb-4 font-bold text-slate-900" data-id="element-226">
          La pregunta fue simple: ¿Y si el problema no es el docente… sino el
          material?
        </p>
        <p className="text-slate-700" data-id="element-227">
          Así nació SIC 360. No como contenido. 👉{' '}
          <span className="font-bold text-blue-600" data-id="element-228">
            Como solución pedagógica.
          </span>
        </p>
      </div>

      <div className="text-center" data-id="element-229">
        <p className="text-xl font-bold text-slate-900 mb-2" data-id="element-230">
          Con SIC 360 podés enseñar todo el ciclo lectivo sin volver a
          improvisar una sola clase.
        </p>
        <p className="text-lg text-slate-600" data-id="element-231">
          Menos preparación. Más claridad. Más seguridad. Más disfrute.
        </p>
      </div>
    </article>;
}